import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { rupiah, todayISO, toDateInput } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Flame } from "lucide-react";
import { rit } from "@/components/app/bakar/StokKayuBar";

const addDays = (iso, n) => {
  if (!iso) return "";
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + Number(n || 0));
  return d.toLocaleDateString("id-ID", { day: "numeric", month: "long", year: "numeric" });
};

export const MulaiBakarDialog = ({ open, setOpen, item, stok, produkList, onSaved }) => {
  const [form, setForm] = useState({});
  useEffect(() => {
    if (!open) return;
    setForm(item
      ? { tanggal_mulai: toDateInput(item.tanggal_mulai), produk_id: item.produk_id, jumlah_rit: item.jumlah_rit, durasi_hari: item.durasi_hari, keterangan: item.keterangan || "" }
      : { tanggal_mulai: todayISO(), produk_id: produkList[0]?.id || "", jumlah_rit: "", durasi_hari: 10, keterangan: "" });
  }, [open, item]); // eslint-disable-line react-hooks/exhaustive-deps

  const tersedia = Number(stok?.sisa_rit || 0) + Number(item?.jumlah_rit || 0);
  const nilai = Number(stok?.nilai_sisa || 0) + Number(item?.biaya_kayu || 0);
  const avg = tersedia > 0 ? nilai / tersedia : 0;
  const jml = Number(form.jumlah_rit || 0);
  const lebih = jml > tersedia + 1e-6;
  const upd = (k) => (e) => setForm({ ...form, [k]: e && e.target ? e.target.value : e });

  const save = async (e) => {
    e?.preventDefault();
    if (!form.tanggal_mulai) { toast.error("Tanggal mulai wajib diisi"); return; }
    if (!(jml > 0)) { toast.error("Jumlah kayu (rit) wajib diisi"); return; }
    if (lebih) { toast.error(`Stok kayu tidak cukup. Tersedia ${rit(tersedia)}`); return; }
    const payload = { tanggal_mulai: form.tanggal_mulai, produk_id: form.produk_id || null, jumlah_rit: jml, durasi_hari: Number(form.durasi_hari || 10), keterangan: form.keterangan || "" };
    try {
      if (item) await api.put(`/batch-bakar/${item.id}`, payload);
      else await api.post("/batch-bakar", payload);
      toast.success(item ? "Batch diperbarui" : "Tungku mulai dibakar");
      setOpen(false); onSaved();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-lg" data-testid="bakar-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Flame size={18} className="text-orange-600"/>{item ? "Ubah Batch Bakar" : "Mulai Bakar (Isi Tungku)"}</DialogTitle></DialogHeader>
        <div className="text-xs bg-emerald-50 border border-emerald-200 rounded-sm p-2 text-emerald-800" data-testid="bakar-stok-info">
          Stok kayu tersedia: <b>{rit(tersedia)}</b> · harga rata-rata <b>{rupiah(Math.round(avg))}</b>/rit
        </div>
        <form onSubmit={save} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2 space-y-1.5">
            <Label>Produk Arang <span className="text-rose-500">*</span></Label>
            <Select value={form.produk_id || ""} onValueChange={upd("produk_id")}>
              <SelectTrigger data-testid="bakar-produk"><SelectValue placeholder="Pilih produk"/></SelectTrigger>
              <SelectContent>{produkList.map((p) => <SelectItem key={p.id} value={p.id}>{p.nama}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label>Tanggal Mulai Bakar <span className="text-rose-500">*</span></Label><Input type="date" value={form.tanggal_mulai || ""} onChange={upd("tanggal_mulai")} data-testid="bakar-tanggal"/></div>
          <div className="space-y-1.5"><Label>Kayu Dipakai (rit) <span className="text-rose-500">*</span></Label>
            <Input type="number" min="0" step="0.01" value={form.jumlah_rit} onChange={upd("jumlah_rit")} placeholder="mis. 0,7 atau 1,3" className={lebih ? "border-rose-400" : ""} data-testid="bakar-rit"/>
            {lebih && <div className="text-[11px] text-rose-600" data-testid="bakar-rit-error">Melebihi stok ({rit(tersedia)})</div>}
          </div>
          <div className="space-y-1.5"><Label>Durasi Bakar (hari)</Label><Input type="number" min="1" value={form.durasi_hari} onChange={upd("durasi_hari")} data-testid="bakar-durasi"/></div>
          <div className="space-y-1.5"><Label>Perkiraan Bongkar</Label><div className="h-9 px-3 flex items-center border border-slate-200 bg-slate-50 text-sm" data-testid="bakar-estimasi">{addDays(form.tanggal_mulai, form.durasi_hari) || "-"}</div></div>
          <div className="md:col-span-2 space-y-1.5"><Label>Biaya Kayu Terpakai (otomatis)</Label>
            <div className="h-9 px-3 flex items-center border border-slate-200 bg-blue-50 font-mono font-semibold brand-text" data-testid="bakar-biaya-kayu">{rupiah(Math.round(jml * avg))}</div>
          </div>
          <div className="md:col-span-2 space-y-1.5"><Label>Keterangan</Label><Textarea value={form.keterangan || ""} onChange={upd("keterangan")} data-testid="bakar-keterangan"/></div>
          <button type="submit" className="hidden" aria-hidden="true"/>
        </form>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Batal</Button>
          <Button onClick={save} className="brand-btn rounded-sm" data-testid="bakar-save">Simpan</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
